import React, { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';

import AMP_KEYS from '@/configs/amp-track-keys';
import CompanyUploadModal from '@/features/Account/components/CompanyUploadModal';
import CustomerUploadModal from '@/features/Contact/CustomerUploadModal';
import AttachmentModal from '@/features/Feedback/components/AttachmentModal';
import CreateFeedbackModal from '@/features/Feedback/components/CreateFeedbackModal';
import TagConfigModal from '@/features/Feedback/field/Tag/TagConfigModal';
import SearchFloat from '@/features/GlobalSearch/components/GlobalSearchFloat/GlobalSearchFloat';
import GlobalSearchModal from '@/features/GlobalSearch/components/GlobalSearchModal';
import useGlobalSearchModalAction from '@/features/GlobalSearch/hooks/useGlobalSearchModalAction';
import useUserRole from '@/hooks/domain/useUserRole';
import useWorkspaceRouter from '@/hooks/domain/useWorkspaceRouter';
import useKeymap from '@/hooks/pure/useKeymap';
import useModalStore from '@/stores/pure/ModalStore';
import { track } from '@/utils/track/track';
import { removeQuery } from '@/utils/util';

import NavigatorFloat from '../../features/Commons/NavigatorFloat/NavigatorFloat';
import Modal from './Modal';
import { FEEDBACK_CREATE_MODAL_KEY, SEARCH_MODAL_KEY } from './modal-keys';

const ModalManager = () => {
  const { isEditor } = useUserRole();
  const { navigate } = useWorkspaceRouter();
  const [searchParams] = useSearchParams();
  const { setSearchStoreState } = useGlobalSearchModalAction();
  const enableNavigatorShortcut = true;

  const {
    tagConfigModal,
    customerUploadModal,
    attachmentModal,
    createFeedbackModal,
    companyUploadModal,
    searchFloatModal,
    navigatorFloatModal,
    setModalStatus,
  } = useModalStore();

  useEffect(() => {
    if (isEditor) {
      const feedbackCreate = searchParams.get(FEEDBACK_CREATE_MODAL_KEY);
      const hasFeedbackCreateQuery = Boolean(feedbackCreate);
      setModalStatus('createFeedbackModal', hasFeedbackCreateQuery);

      const searchModalParams = searchParams.get(SEARCH_MODAL_KEY);
      if (searchModalParams) {
        setSearchStoreState({
          status: true,
          alias: searchModalParams,
        });
      } else {
        setSearchStoreState({
          status: false,
        });
      }
    }
  }, [searchParams, isEditor, setModalStatus, setSearchStoreState]);

  const closeModal = (modal_key: string) => {
    navigate(
      `${location.pathname}${removeQuery(
        Object.fromEntries([...searchParams]),
        modal_key,
      )}`,
    );
  };

  useKeymap(
    'KeyE',
    () => {
      track(AMP_KEYS.SEARCH_SHORTCUT);
      setModalStatus('searchFloatModal', true);
    },
    {
      ctrl: true,
    },
  );

  useKeymap(
    enableNavigatorShortcut ? 'KeyP' : undefined,
    enableNavigatorShortcut
      ? () => {
          track(AMP_KEYS.NAVIGATOR_SHORTCUT);
          setModalStatus('navigatorFloatModal', true);
        }
      : undefined,
    {
      ctrl: true,
    },
  );

  return (
    <React.Fragment>
      <GlobalSearchModal />
      <Modal
        status={customerUploadModal}
        closeModal={() => {
          setModalStatus('customerUploadModal', false);
          navigate(-1);
        }}
      >
        <CustomerUploadModal />
      </Modal>
      <Modal
        status={attachmentModal}
        closeModal={() => setModalStatus('attachmentModal', false)}
        useBackgroundClose
      >
        <AttachmentModal />
      </Modal>
      <Modal
        status={searchFloatModal}
        closeModal={() => setModalStatus('searchFloatModal', false)}
        useBackgroundClose
      >
        <SearchFloat />
      </Modal>
      <Modal
        status={navigatorFloatModal}
        closeModal={() => setModalStatus('navigatorFloatModal', false)}
        useBackgroundClose
      >
        <NavigatorFloat />
      </Modal>
      <CreateFeedbackModal
        status={createFeedbackModal}
        close={() => closeModal('feedback-create')}
      />
      <Modal
        status={companyUploadModal}
        closeModal={() => {
          setModalStatus('companyUploadModal', false);
          navigate(-1);
        }}
      >
        <CompanyUploadModal />
      </Modal>
      <Modal
        status={tagConfigModal}
        closeModal={() => setModalStatus('tagConfigModal', false)}
        useBackgroundClose
      >
        <TagConfigModal />
      </Modal>
    </React.Fragment>
  );
};

export default ModalManager;
